// The whole-course diagram, on a course's page in the library.
//
// Same palette and the same flat illustrated look as `HolePlan`, but its own class
// prefix rather than a shared one: at course scale an outline drawn 2.5 units thick
// would be nine yards of ink, and the shapes would disappear under their own edges.
// Everything here is thinner, and the tee markers are dropped entirely — a tee box is
// about a unit across on a whole property and a marker inside it would be noise.
//
// It zooms, because at rest it has to. Two and a half thousand yards across a phone is
// six yards to the pixel: the cart path is half a pixel wide and a bunker is a smudge,
// and no amount of restyling fixes that — the only cure for detail lost to scale is
// less scale. Wheel or pinch to go in, drag to move, and the labels hold their size
// while the ground grows under them.
import { html } from "htm/preact";
import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";
import type { CourseMap, ShapeKind } from "../../../shared/types.js";
import { coursePlan } from "../../../shared/coursePlan.js";
import { smoothPath, type PlanPath } from "../../../shared/holePlan.js";
import { OSM_ATTRIBUTION } from "../../../shared/overpass.js";
import { Icon } from "./Icon.js";

/** Painting order, bottom to top. Same reasoning as the hole diagram. */
const LAYER_ORDER: ShapeKind[] = ["fairway", "apron", "water", "tee", "green", "bunker", "path"];

/**
 * Closest you can get.
 *
 * Eight times in means roughly a yard to the pixel on a phone, which is closer than the
 * traced geometry deserves to be looked at — past this you are reading the click
 * spacing of the outline rather than the course.
 */
const MAX_ZOOM = 8;

/** How much of the casing shows either side of a cart path, in CSS pixels. */
const PATH_CASING_PX = 0.9;

interface View {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** One element per distinct width, so a path network never doubles its own stroke. */
function cartPathGroups(paths: PlanPath[]): { widthUnits: number; d: string }[] {
  const byWidth = new Map<number, string[]>();
  for (const p of paths) {
    const key = Math.round(p.widthUnits * 100) / 100;
    const list = byWidth.get(key) ?? [];
    list.push(smoothPath(p.points, false));
    byWidth.set(key, list);
  }
  return [...byWidth.entries()].map(([widthUnits, ds]) => ({ widthUnits, d: ds.join(" ") }));
}

export function CoursePlan({ map }: { map: CourseMap }) {
  // Memoised because a drag re-renders on every pointer frame, and neither the
  // projection nor the smoothed path strings depend on where the view is looking.
  const plan = useMemo(() => coursePlan(map), [map]);
  const drawn = useMemo(() => {
    if (!plan) return null;
    return {
      shapes: [...plan.shapes]
        .sort((a, b) => LAYER_ORDER.indexOf(a.kind) - LAYER_ORDER.indexOf(b.kind))
        .map((s) => ({ kind: s.kind, d: smoothPath(s.points, s.closed) })),
      groups: cartPathGroups(plan.paths),
    };
  }, [plan]);

  const full: View = { x: 0, y: 0, w: plan?.width ?? 1, h: plan?.height ?? 1 };

  const svgRef = useRef<SVGSVGElement | null>(null);
  const [view, setView] = useState<View>(full);

  // A different course is a different frame; anything else would open it mid-zoom on
  // whatever corner the last one was left at.
  useEffect(() => {
    setView({ x: 0, y: 0, w: plan?.width ?? 1, h: plan?.height ?? 1 });
  }, [map.mapId, plan?.width, plan?.height]);

  /** Keeps the view inside the drawing and inside the zoom range. */
  const clamp = useCallback(
    (next: View): View => {
      const minW = full.w / MAX_ZOOM;
      const w = Math.min(Math.max(next.w, minW), full.w);
      // Height follows width, so the aspect ratio — and therefore the scale on both
      // axes — never changes. Letting them drift apart would misreport every distance.
      const h = (w / full.w) * full.h;
      return {
        w,
        h,
        x: Math.min(Math.max(next.x, 0), full.w - w),
        y: Math.min(Math.max(next.y, 0), full.h - h),
      };
    },
    [full.w, full.h],
  );

  /** Zoom about a point given in client coordinates, so what's under the pointer stays. */
  const zoomAt = useCallback(
    (clientX: number, clientY: number, factor: number) => {
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect || rect.width === 0) return;
      setView((v) => {
        const ux = v.x + ((clientX - rect.left) / rect.width) * v.w;
        const uy = v.y + ((clientY - rect.top) / rect.height) * v.h;
        const next = clamp({ ...v, w: v.w / factor });
        // Put the grabbed point back where it was, in the new scale.
        return clamp({
          ...next,
          x: ux - ((clientX - rect.left) / rect.width) * next.w,
          y: uy - ((clientY - rect.top) / rect.height) * next.h,
        });
      });
    },
    [clamp],
  );

  /** Zoom about the middle, for the buttons and the keyboard. */
  const zoomCenter = useCallback(
    (factor: number) => {
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;
      zoomAt(rect.left + rect.width / 2, rect.top + rect.height / 2, factor);
    },
    [zoomAt],
  );

  /* ------------------------------------------------------------------ gestures */

  // Live pointers, for telling a drag from a pinch. In a ref because the handlers are
  // installed once and a pinch must not re-run them between frames.
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinchDistance = useRef(0);

  const onPointerDown = (e: PointerEvent) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinchDistance.current = Math.hypot(a!.x - b!.x, a!.y - b!.y);
    }
  };

  const onPointerMove = (e: PointerEvent) => {
    const previous = pointers.current.get(e.pointerId);
    if (!previous) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()];
      const distance = Math.hypot(a!.x - b!.x, a!.y - b!.y);
      if (pinchDistance.current > 0 && distance > 0) {
        zoomAt((a!.x + b!.x) / 2, (a!.y + b!.y) / 2, distance / pinchDistance.current);
      }
      pinchDistance.current = distance;
      return;
    }

    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    // Drag moves the ground with the finger, so the view moves the other way.
    const dx = ((e.clientX - previous.x) / rect.width) * view.w;
    const dy = ((e.clientY - previous.y) / rect.height) * view.h;
    setView((v) => clamp({ ...v, x: v.x - dx, y: v.y - dy }));
  };

  const onPointerUp = (e: PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchDistance.current = 0;
  };

  // Wheel is registered by hand rather than as a prop: it has to be non-passive to
  // preventDefault, and a passive listener would scroll the page as well as zoom.
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return undefined;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      zoomAt(e.clientX, e.clientY, Math.exp(-e.deltaY * 0.0015));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoomAt]);

  if (!plan || !drawn) return null;
  const { shapes, groups } = drawn;

  // User units per rendered pixel, relative to the unzoomed view. Anything that should
  // hold its size on screen — labels, marker dots, the casing either side of a path —
  // is multiplied by this; anything that is a real distance on the ground is not.
  const k = view.w / full.w;
  const zoomed = k < 0.999;

  return html`
    <div class="cp-frame">
      <svg
        class=${`course-plan ${zoomed ? "is-zoomed" : ""}`}
        ref=${svgRef}
        viewBox=${`${view.x} ${view.y} ${view.w} ${view.h}`}
        role="img"
        aria-label=${`Plan of ${map.courseName || "the course"}, ${plan.holes.length} of ${map.holes.length || plan.holes.length} holes traced`}
        onPointerDown=${onPointerDown}
        onPointerMove=${onPointerMove}
        onPointerUp=${onPointerUp}
        onPointerCancel=${onPointerUp}
        onDblClick=${() => setView(full)}
      >
        ${/* Same trap as the hole diagram: the drawing is letterboxed inside the element
             whenever `max-height` binds, and the bars that leaves are inside the SVG
             viewport, so nothing clips them. The ground they want is the element's own
             background; everything drawn is clipped to the frame. */ ""}
        <clipPath id="cp-clip">
          <rect x="0" y="0" width=${full.w} height=${full.h} />
        </clipPath>

        <g clip-path="url(#cp-clip)">
        ${shapes.map((s, i) => html`<path key=${i} class=${`cp-${s.kind}`} d=${s.d} />`)}

        ${/* Cart paths, cased: a darker line under a lighter one. At course scale a
             three-yard path is about a pixel of pale grey on pale green and simply
             isn't there — the casing is what a printed map uses to make a thin line
             read, and it costs one more element per width.

             Every casing is drawn before every fill, rather than casing-then-fill per
             group, so a wide path's casing can't paint over a narrower one's surface
             where the two run together. */
        groups.map(
          (g) => html`
            <path
              key=${`cs-${g.widthUnits}`}
              class="cp-cartpath-casing"
              stroke-width=${g.widthUnits + PATH_CASING_PX * 2 * k}
              d=${g.d}
            />
          `,
        )}
        ${groups.map(
          (g) => html`
            <path key=${`cp-${g.widthUnits}`} class="cp-cartpath" stroke-width=${g.widthUnits} d=${g.d} />
          `,
        )}

        ${plan.landmarks.map(
          (l, i) => html`
            <g key=${`lm-${i}`} class=${`cp-landmark cp-lm-${l.kind}`}>
              <circle cx=${l.at.x} cy=${l.at.y} r=${3.5 * k} />
              <text
                x=${l.at.x}
                y=${l.at.y - 6 * k}
                font-size=${7.5 * k}
                stroke-width=${2.5 * k}
              >
                ${l.label}
              </text>
            </g>
          `,
        )}

        ${/* ODbL asks for this wherever the data is shown, and a course map may carry
             outlines and cart paths that were imported rather than traced. Pinned to
             the unzoomed frame and scaled by `k`, so it stays put and stays small
             however far in you go. */
        html`<text class="cp-credit" x=${full.w - 4} y=${full.h - 4} font-size=${7 * k}>
          ${OSM_ATTRIBUTION}
        </text>`}

        ${/* Hole numbers, last so nothing is drawn over them. This is what turns a green
             blob into "the 12th" and is the reason anyone opens this picture. */
        plan.holes.map(
          (h) => html`
            <g key=${h.hole} class="cp-hole">
              ${h.radius !== null &&
              html`<circle class="cp-green" cx=${h.at.x} cy=${h.at.y} r=${h.radius} />`}
              <text x=${h.at.x} y=${h.at.y} font-size=${11 * k} stroke-width=${3 * k}>${h.hole}</text>
            </g>
          `,
        )}
        </g>
      </svg>

      <div class="cp-zoom">
        <button
          class="icon-btn"
          aria-label="Zoom in"
          disabled=${view.w <= full.w / MAX_ZOOM + 1e-6}
          onClick=${() => zoomCenter(1.6)}
        >
          <${Icon} name="plus" size=${16} />
        </button>
        <button class="icon-btn" aria-label="Zoom out" disabled=${!zoomed} onClick=${() => zoomCenter(1 / 1.6)}>
          <${Icon} name="minus" size=${16} />
        </button>
        ${zoomed &&
        html`<button class="ghost cp-reset" onClick=${() => setView(full)}>Whole course</button>`}
      </div>
    </div>
  `;
}
